import React, { VFC } from 'react';
import styled from 'styled-components';
import { Container, Row, Col, useScreenClass } from 'react-grid-system';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../utils/theme';
import PrimaryButton from '../molecules/PrimaryButton';
import SecondaryButton from '../molecules/SecondaryButton';
import Typography from '../atoms/Typography';
import ProductImage from '../molecules/ProductImage';
import Spacing from '../atoms/Spacing';
import SvgIcon from '../atoms/SvgIcon';
import { useReturnContext } from '../../contexts/returnContext';
import CrossIcon from '../atoms/icons/CrossIcon';
import TableItem from '../molecules/TableItem';
import { EASYCOM_ROUTES } from '../../navigation';
import Divider from '../atoms/Divider';
import { useSettings } from '../../contexts/settingsContext';
import {
  tReturnBasketItem,
  tUpdateAndAddToReturnArgs,
} from '../../contexts/zod';

interface Props {
  radius?: string;
  top?: string;
}

const ProductBasketStyle = styled(Container)<Props>`
  position: absolute !important;
  z-index: 10;
  min-width: 360px;
  right: 0px;
  // top: 56px;
  top: ${({ top }) => top};
  background: #ffffff;
  box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.1);
  border-radius: ${({ radius }) => radius};
`;

const Close = styled.div`
  cursor: pointer;
`;

const ProductBasket: VFC<Props> = ({
  showTotalNumberOfItems,
  ...props
}) => {
  const { cornerRadius } = useSettings();
  const navigate = useNavigate();
  const screenClass = useScreenClass();
  const { returns, showSpecificReturn, setShowSpecificReturn } =
    useReturnContext();

  const sumOfProducts = Object.keys(returns).reduce(
    (previousValue, currentValue) =>
      previousValue + returns[currentValue].returnData.amount,
    0,
  );

  const currentReturn: tUpdateAndAddToReturnArgs = returns[showSpecificReturn];

  const goToReturnPage = () => {
    setShowSpecificReturn(undefined);
    navigate(EASYCOM_ROUTES.RETURNS);
  };

  const shouldBeBigger = ['xs', 'sm', 'md'].includes(screenClass);

  return (
    <ProductBasketStyle
      radius={cornerRadius}
      top={shouldBeBigger ? '76px' : '56px'}
    >
      <Spacing spacing={3} />
      <Row align="center" justify="end">
        <Col>
          <Row nogutter justify="end">
            <Close onClick={() => setShowSpecificReturn(undefined)}>
              <CrossIcon color="black" size={16} />
            </Close>
          </Row>
        </Col>
      </Row>
      <Spacing spacing={3} />
      <Row align="center" justify="start">
        <Col>
          <Row nogutter align="center">
            <SvgIcon
              icon="check-with-circle"
              iconSize="large"
              color={theme.colors.green[100]}
            />
            <Spacing horizontal />
            <Typography translateKey="return_added" variant="h5" />
          </Row>
        </Col>
      </Row>
      <Spacing spacing={3} />
      <Row>
        <Col sm={3} md={3} lg={3}>
          <Row nogutter>
            <ProductImage src={currentReturn.imageUrl} />
          </Row>
        </Col>
      </Row>
      <Spacing spacing={3} />
      <Row>
        <Col>
          <TableItem
            title={
              <Typography translateKey="art_no" variant="body1-semi-bold" />
            }
            value={`${currentReturn.productNumber}${currentReturn.variant}`}
          />
        </Col>
      </Row>
      <Spacing />
      <Divider />
      <Spacing />
      <Row>
        <Col>
          <TableItem
            title={
              // eslint-disable-next-line react/jsx-wrap-multilines
              <Typography
                translateKey="no_of_products"
                variant="body1-semi-bold"
              />
            }
            value={showTotalNumberOfItems ? sumOfProducts : currentReturn.returnData.amount}
          />
        </Col>
      </Row>
      <Spacing spacing={3} />
      <Row>
        <Col>
          <Row nogutter>
            <Col xs={4} sm={4}>
              <SecondaryButton
                translateKey="close"
                onClick={() => setShowSpecificReturn(undefined)}
              />
            </Col>
            <Col>
              <PrimaryButton
                translateKey="go_to_return_basket"
                icon="arrow-right"
                onClick={goToReturnPage}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Spacing spacing={4} />
    </ProductBasketStyle>
  );
};

export default ProductBasket;
